import { createFileRoute } from '@tanstack/react-router';
import { MarketProductTemplatePending } from '@/components/v2/page-template-pending';
import { cacheFirst } from '@/lib/queries/cache-first';
import { marketProductQuery, type MarketProductQueryData } from '@/lib/queries/detail';
import { entityHeadMeta } from '@/lib/utils/site-meta';
import { toMarketProductResult, type MarketProductResult } from './-market-loader-data';

export const Route = createFileRoute('/_authenticated/market/$productId')({
  // Template-first pending: while this route's component chunk downloads, show the
  // destination's page shape instead of the full-page RoutePending mascot
  // (audit part 2: a detail click must switch immediately, skeletons included).
  // The product page keeps the back header and opens on a text hero + variant
  // grid + sticky Buy bar — not the generic media-hero shape.
  pendingComponent: MarketProductTemplatePending,
  loader: async ({ context, params, preload }) => {
    // Key + queryFn come from the shared factory (app/lib/queries/detail.ts) —
    // the same entry useMarketProduct (same key) reads, so the cache entry
    // always holds the queryFn's unwrapped product. Behavior is byte-for-byte
    // the inline unwrap-throw shape this used to spell out.
    const productQuery = marketProductQuery(params.productId);
    const fetchProduct = () => context.queryClient.ensureQueryData(productQuery);
    // Preload short-circuit: detail-entry cards fire this loader via
    // <Link preload="viewport"> as they scroll past, and a preload run must
    // never touch the network. Serve the cache when present, else an
    // instantly-resolved cold ({ ok: false }) envelope. The result must
    // SETTLE, never park: preloadRoute keeps the loader lane, its abort
    // controller and match resources alive until the loader settles, so a
    // never-resolving promise would leak a permanently-pending lane for
    // every card scrolled past. The ok:false value is never rendered —
    // commits always re-run this loader (defaultPreloadStaleTime is 0);
    // warming the route chunk is all a preload exists for here.
    // getQueryData is optional-chained for the loader-contract tests, which
    // stub the client with ensureQueryData only; a missing reader simply
    // reads as cold cache.
    if (preload) {
      const cached = context.queryClient.getQueryData?.<MarketProductQueryData>(
        productQuery.queryKey
      );
      return {
        productDetailPromise:
          cached === undefined
            ? Promise.resolve<MarketProductResult<MarketProductQueryData>>({ ok: false })
            : toMarketProductResult(Promise.resolve(cached)),
        productDetail: undefined,
      };
    }
    // Cache-first gate: the shared gate (app/lib/queries/data-plane, through
    // cacheFirst) owns the freshness decision, so this loader and the
    // prefetchers can never disagree about whether the cache entry is usable.
    // A cached product resolves the envelope immediately and a stale entry is
    // healed in the background (single-flight-deduped with useMarketProduct's
    // mounted query); a cold cache keeps the streamed fetch below, with its
    // existing retry/error paths.
    const { cached, stream } = cacheFirst(context.queryClient, productQuery.queryKey, fetchProduct);
    const productDetailPromise =
      cached !== undefined
        ? toMarketProductResult(Promise.resolve(cached))
        : toMarketProductResult(stream());
    // Server-only await so head() can read the product for SSR OG tags (audit
    // UIUX-02); failures resolve to ok:false so the body keeps its error UI
    // instead of the error boundary. Client navigations skip the await — the
    // component (useMarketProduct, same key) renders its own skeleton from the
    // primed query and head() falls back to the root defaults (perf audit
    // part 2, Theme 2).
    if (typeof document !== 'undefined') {
      return { productDetailPromise, productDetail: undefined };
    }
    const productDetail = await productDetailPromise;
    return {
      productDetailPromise,
      productDetail: productDetail.ok ? productDetail.data : undefined,
    };
  },
  head: ({ loaderData, params }) => {
    const product = loaderData?.productDetail;
    if (!product) return {};
    return {
      ...entityHeadMeta({
        title: `${product.title} - Market | TIL`,
        description:
          product.description ||
          `Buy the ${product.title} pass on TIL and redeem classes across studios.`,
        // MarketProduct carries no slug field yet, so the id stays canonical.
        path: `/market/${params.productId}`,
      }),
    };
  },
});
